import { useTranslation } from 'react-i18next';
import type { CatalogRow } from '@/lib/catalog';

interface Props {
  rows: CatalogRow[];
  onOpen: (sku: string) => void;
}

const n0 = (v: number | null) => (v == null ? '—' : v.toFixed(0));

export default function ProductTable({ rows, onOpen }: Props) {
  const { t } = useTranslation();

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="max-h-[calc(100vh-260px)] overflow-auto thin-scroll">
        <table className="w-full min-w-[960px] border-collapse text-[12px]">
          <thead className="sticky top-0 z-10 bg-background-100 text-left text-[10px] uppercase tracking-[0.07em] text-foreground-500">
            <tr className="border-b border-background-200">
              <th className="px-3 py-2 font-medium">{t('catalog.colItem')}</th>
              <th className="px-2 py-2 font-medium">{t('catalog.colCategory')}</th>
              <th className="px-2 py-2 font-medium">{t('catalog.colSource')}</th>
              <th className="px-2 py-2 text-right font-medium">{t('cabinet.colWeight')}</th>
              <th className="px-2 py-2 text-right font-medium">{t('catalog.colStock')}</th>
              <th className="px-2 py-2 text-right font-medium">{t('catalog.colCost')}</th>
              <th className="px-2 py-2 text-right font-medium">{t('catalog.colSale')}</th>
              <th className="px-2 py-2 text-right font-medium">{t('catalog.colMargin')}</th>
              <th className="px-3 py-2 font-medium">{t('catalog.colStorefront')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.sku} onClick={() => onOpen(p.sku)} className="group cursor-pointer border-b border-background-200/70 transition-colors last:border-0 hover:bg-primary-50/40">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border border-background-200 bg-white">
                      {p.image ? <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-contain" /> : <i className="ri-image-line text-[14px] text-foreground-300" />}
                    </div>
                    <div className="min-w-0">
                      <p className="tnum text-[10.5px] text-foreground-500">
                        {p.sku}
                        {p.supplierArticle && p.supplierArticle !== p.sku ? ` · ${p.supplierArticle}` : ''}
                      </p>
                      <p className="max-w-[230px] truncate font-medium text-foreground-900 group-hover:text-primary-700">{p.name}</p>
                      <p className="text-[10.5px] text-foreground-400">{[p.color, p.base].filter(Boolean).join(' · ') || (p.partnerName ?? '')}</p>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2 text-foreground-600">{t(`category.${p.category}`, { defaultValue: p.category })}</td>
                <td className="px-2 py-2">
                  <span className="whitespace-nowrap rounded border border-background-200 bg-background-100 px-1.5 py-0.5 text-[10px] font-medium text-foreground-600" title={p.partnerName ?? undefined}>
                    {p.source === 'Own' ? t('status.Own warehouse') : p.partnerName ?? t('status.Partner')}
                  </span>
                </td>
                <td className="tnum px-2 py-2 text-right text-foreground-500">{p.weightKg != null ? `${p.weightKg} kg` : '—'}</td>
                <td className="px-2 py-2 text-right">
                  {p.stock > 0 ? (
                    <span className="tnum font-medium text-foreground-900">{p.stock}</span>
                  ) : (
                    <span className="tnum rounded bg-accent-50 px-1.5 py-0.5 text-[10.5px] font-medium text-accent-900">0 · +{p.incomingQty}</span>
                  )}
                </td>
                <td className="tnum px-2 py-2 text-right text-foreground-500">{n0(p.cost)}</td>
                <td className="tnum px-2 py-2 text-right font-semibold text-foreground-950">{n0(p.sale)}</td>
                <td className="px-2 py-2 text-right">
                  <span className="tnum text-[11.5px] font-medium text-primary-700">{p.marginPct != null ? `${p.marginPct.toFixed(1)}%` : '—'}</span>
                  <span className="tnum block text-[10px] text-foreground-400">{p.marginAed != null ? `${p.marginAed.toFixed(0)} AED` : ''}</span>
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5 text-[10.5px] font-medium">
                    <span className={`h-1.5 w-1.5 rounded-full ${p.published ? 'bg-primary-500' : 'bg-background-400'}`} />
                    <span className={p.published ? 'text-foreground-700' : 'text-foreground-400'}>{p.published ? t('status.Published') : t('status.Hidden')}</span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
